import { useCallback } from 'react';
import {
  GamesListQuery,
  GamesListQueryVariables,
  useGamesListQuery,
} from '~/client/shared/game/GameList/graphql/GamesListQuery.generated';

const useGameList = (variables: GamesListQueryVariables, skip?: boolean) => {
  const { data, fetchMore, loading } = useGamesListQuery({
    variables,
    skip,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const getDataSelector = useCallback(() => {
    if (data?.casino?.myFavourites) {
      return 'myFavourites';
    }
    if (data?.casino?.myRecents) {
      return 'myRecents';
    }
    return 'games';
  }, [data?.casino?.myFavourites, data?.casino?.myRecents]);

  const gameList = data?.casino?.[getDataSelector()]?.edges;
  const totalCount = data?.casino?.[getDataSelector()]?.totalCount ?? 0;
  const endCursor = data?.casino?.[getDataSelector()]?.pageInfo?.endCursor;
  const step = variables.first;

  const onLoadMore = useCallback(() => {
    fetchMore({
      variables: {
        ...variables,
        last: step,
        cursor: endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        const { casino } = fetchMoreResult;
        const { games, myFavourites, myRecents } = fetchMoreResult.casino ?? {};

        const newResult = {
          casino: {
            ...casino,
            games: {
              ...games,
              edges: [
                ...(prev?.casino?.games?.edges ?? []),
                ...(fetchMoreResult?.casino?.games?.edges ?? []),
              ],
            },
            myFavourites: {
              ...myFavourites,
              edges: [
                ...(prev?.casino?.myFavourites?.edges ?? []),
                ...(fetchMoreResult?.casino?.myFavourites?.edges ?? []),
              ],
            },
            myRecents: {
              ...myRecents,
              edges: [
                ...(prev?.casino?.myRecents?.edges ?? []),
                ...(fetchMoreResult?.casino?.myRecents?.edges ?? []),
              ],
            },
          },
        };
        return newResult as GamesListQuery;
      },
    });
  }, [endCursor, fetchMore, variables, step]);

  return { gameList, totalCount, onLoadMore, loading, step };
};

export default useGameList;
